import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import './app-shell-flow-rail.css'
import { Link, useNavigate } from '@tanstack/react-router'
import {
  Captions,
  ChevronDown,
  Clapperboard,
  Film,
  Folder,
  FolderKanban,
  Home,
  Link2,
  PanelsTopLeft,
  Pause,
  Plus,
  Sparkles,
  Zap,
} from 'lucide-react'
import { JianLogo } from '@/components/brand/jian-logo'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { LocalMcpStatusPanel } from '@/features/settings/components/local-mcp-status-panel'
import { AuthUserMenu } from '@/features/auth/components/auth-user-menu'
import { NotificationInbox } from '@/features/notifications/components/notification-inbox'
import { fetchMembershipSummary } from '@/features/auth/services/auth-service'
import { useAuthSessionStore } from '@/features/auth/stores/auth-session-store'
import { useSettingsStore } from '@/features/settings/stores/settings-store'
import { useStudioStore } from '@/features/app-shell/stores/studio-store'
import { WorkspaceUpgradeDialog } from '@/features/billing/components/workspace-upgrade-dialog'
import {
  calcAiUsagePercent,
  formatMembershipPlan,
  getPlanAiProcessingSeconds,
} from '@/features/billing/utils/membership-labels'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { appFetch } from '@/shared/api/app-client'
import type { ApiResponse, Studio } from '@/shared/api/app-types'
import { cn } from '@/shared/ui/cn'

type AppShellProps = {
  children: ReactNode
  activePath: string
}

const mainLinks = [
  { label: '首页', to: '/app', icon: Home },
  { label: '项目', to: '/app/projects', icon: FolderKanban },
  {
    label: '品牌模板',
    to: '/app/branding',
    icon: PanelsTopLeft,
    badge: 'PRO',
  },
  { label: '素材库', to: '/app/assets', icon: Folder },
] as const

function SidebarLink({
  to,
  icon: Icon,
  label,
  active,
  badge,
}: {
  to: string
  icon: React.ComponentType<{ className?: string }>
  label: string
  active?: boolean
  badge?: string
}) {
  return (
    <Link
      to={to}
      className={cn(
        'group flex items-center gap-x-3 rounded-lg px-2 py-1.5 text-sm font-medium transition-colors',
        active
          ? 'bg-accent text-foreground'
          : 'text-muted-foreground hover:bg-accent hover:text-foreground',
      )}
    >
      <Icon
        className={cn(
          'size-4 shrink-0',
          active ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground',
        )}
      />
      <span className="truncate">{label}</span>
      {badge ? (
        <span className="ml-auto rounded-full border border-border bg-muted px-1.5 py-0 text-[10px] font-bold tracking-wide text-muted-foreground">
          {badge}
        </span>
      ) : null}
    </Link>
  )
}

function SidebarAction({
  icon: Icon,
  label,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full items-center gap-x-3 rounded-lg px-2 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
    >
      <Icon className="size-4 shrink-0 text-muted-foreground group-hover:text-foreground" />
      <span className="truncate">{label}</span>
    </button>
  )
}

export function AppShell({ children, activePath }: AppShellProps) {
  const navigate = useNavigate()
  const session = useAuthSessionStore((state) => state.session)
  const updateMembership = useAuthSessionStore((state) => state.updateMembership)
  const studio = useStudioStore((state) => state.currentStudio)
  const setStudio = useStudioStore((state) => state.setCurrentStudio)
  const [mcpDialogOpen, setMcpDialogOpen] = useState(false)
  const [editorEntryDialogOpen, setEditorEntryDialogOpen] = useState(false)
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false)
  const showInterfaceAnimations = useSettingsStore((state) => state.showInterfaceAnimations)
  const setSetting = useSettingsStore((state) => state.setSetting)

  // 进入 app 时拉取一次最新额度，确保侧边栏剩余用量是实时的。
  useEffect(() => {
    void fetchMembershipSummary()
      .then((membership) => {
        if (membership) updateMembership(membership)
      })
      .catch(() => {
        // 拉取失败时保留登录态里的额度
      })
  }, [updateMembership])

  // 拉取当前 Studio 信息，侧边栏工作区切换器显示真实名称。
  useEffect(() => {
    if (studio) return

    let cancelled = false
    void appFetch<ApiResponse<Studio>>('/api/studios/current')
      .then((res) => {
        if (!cancelled) setStudio(res.data)
      })
      .catch(() => {
        // 拉取失败时保留默认占位
      })
    return () => {
      cancelled = true
    }
  }, [setStudio, studio])

  const membership = session?.membership
  const planCode = membership?.planCode ?? null
  const planLabel = formatMembershipPlan(membership?.planName, membership?.planCode)
  // 体验额度的实际总量由后端配额策略决定（例如新用户仅有 10 分钟），
  // 不能用套餐展示用的默认 2 小时替代，否则会把未使用的体验额度错误显示为已用 91%。
  const aiProcessingQuota = membership?.quotas?.details?.ai_processing_seconds
  const totalSeconds = aiProcessingQuota?.total ?? getPlanAiProcessingSeconds(planCode)
  const remainingSeconds = aiProcessingQuota?.available ?? membership?.quotas?.aiProcessingSecondsRemaining ?? null
  const hasTotal = totalSeconds != null && totalSeconds > 0
  const usagePercent = hasTotal ? calcAiUsagePercent(totalSeconds, remainingSeconds) : null
  const showSidebarUpgrade = usagePercent !== null && usagePercent >= 60

  const studioName = studio?.displayName ?? '我的工作区'
  const studioInitials = studioName.slice(0, 2).toUpperCase()

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Sidebar */}
      <div className="hidden w-[230px] shrink-0 flex-col border-r border-border bg-background lg:fixed lg:inset-y-0 lg:z-50 lg:flex">
        <div className="flex grow flex-col gap-y-3 overflow-y-auto px-3">
          {/* logo */}
          <div className="flex h-12 shrink-0 items-center pl-3">
            <JianLogo size="md" />
          </div>

          {/* workspace switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="mb-1 flex min-h-[52px] w-full items-center gap-2 rounded-xl border border-border bg-card p-2 text-left transition-colors hover:border-primary/30"
              >
                <div className="flex size-[30px] shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium leading-none text-primary">
                  {studioInitials}
                </div>
                <span className="max-w-[124px] truncate text-sm font-semibold leading-[1.3] text-foreground">
                  {studioName}
                </span>
                <ChevronDown className="ml-auto size-4 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="bottom" className="w-[206px]">
              <DropdownMenuItem onSelect={() => navigate({ to: '/' })} className="gap-3">
                <Home className="size-[18px]" />
                返回 JIAN 官网
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* create project */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="inline-flex h-9 items-center justify-center gap-2 rounded-full bg-primary text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              >
                <Plus className="size-4" />
                创建项目
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="bottom" className="w-48">
              <DropdownMenuItem
                onSelect={() => navigate({ to: '/app', search: { 'project-type': 'clip' } })}
                className="gap-3"
              >
                <Clapperboard className="size-[18px]" />
                创建片段
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => navigate({ to: '/app', search: { 'project-type': 'caption' } })}
                className="gap-3"
              >
                <Captions className="size-[18px]" />
                添加字幕
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* nav */}
          <ul className="flex flex-1 flex-col gap-y-7">
            <div className="space-y-1">
              {mainLinks.map((item) => (
                <SidebarLink
                  key={item.to}
                  to={item.to}
                  icon={item.icon}
                  label={item.label}
                  active={activePath === item.to}
                  badge={'badge' in item ? item.badge : undefined}
                />
              ))}
            </div>
            <div className="-mx-1 my-3 border-t border-border" />
            <div className="space-y-1">
              <SidebarLink
                to="/app/ai-create"
                icon={Sparkles}
                label="AI 创作"
                active={activePath === '/app/ai-create'}
              />
              <SidebarAction
                icon={Film}
                label="视频剪辑"
                onClick={() => setEditorEntryDialogOpen(true)}
              />
              <SidebarAction icon={Link2} label="MCP" onClick={() => setMcpDialogOpen(true)} />
            </div>
            <div className="-mx-1 my-3 border-t border-border" />
          </ul>

          {/* 用量达到 60% 后才提示升级，避免低用量时占据侧边栏空间。 */}
          {showSidebarUpgrade ? (
            <div className="mt-auto flex flex-col gap-1.5 rounded-lg bg-card p-3">
              <div className="flex items-center justify-between text-foreground">
                <span className="text-sm font-semibold">{planLabel}</span>
              </div>
              <div className="text-[13px] text-muted-foreground">
                {hasTotal && remainingSeconds != null
                  ? `剩余 ${Math.round((remainingSeconds / 60) * 10) / 10} 分钟 AI 额度`
                  : '额度信息加载中'}
              </div>
              <div className="h-1.5 w-full rounded-full bg-muted">
                <div
                  className="h-1.5 rounded-full bg-primary transition-all"
                  style={{ width: `${usagePercent ?? 0}%` }}
                />
              </div>
              <div className="text-[10px] text-muted-foreground">已使用 {usagePercent}%</div>
              <button
                type="button"
                onClick={() => setUpgradeDialogOpen(true)}
                className="mt-1.5 inline-flex h-8 items-center justify-center gap-1.5 rounded-full bg-foreground text-xs font-medium text-background transition-colors hover:bg-foreground/90"
              >
                <Zap className="size-3.5" />
                升级
              </button>
            </div>
          ) : null}

          {/* footer */}
          <div className="flex items-center justify-center gap-3 border-t border-border py-4">
            <Link
              to="/app/help"
              className="text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              帮助
            </Link>
            <Link
              to="/app/privacy"
              className="text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              隐私
            </Link>
            <Link
              to="/app/terms"
              className="text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              条款
            </Link>
          </div>
        </div>
      </div>

      {/* main column */}
      <div className="flex min-h-screen flex-col lg:pl-[230px]">
        {/* top bar */}
        <div className="relative sticky top-0 z-40 flex h-12 shrink-0 items-center gap-x-4 border-b border-border bg-background/95 px-4 backdrop-blur">
          <div className="hidden flex-1 lg:flex" />
          <div className="flex shrink-0 items-center gap-x-2 sm:gap-x-6 lg:gap-x-8">
            <button
              type="button"
              onClick={() => setUpgradeDialogOpen(true)}
              className="inline-flex h-9 items-center gap-1.5 rounded-full bg-primary px-3.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            >
              <Zap className="size-3.5" />
              <span className="hidden sm:inline">升级</span>
            </button>
            <NotificationInbox />
            <AuthUserMenu />
          </div>
          {showInterfaceAnimations ? (
            <div className="app-shell-flow-control">
              <div className="app-shell-flow-rail" aria-hidden="true"><span /></div>
              <button
                type="button"
                onClick={() => setSetting('showInterfaceAnimations', false)}
                className="app-shell-flow-toggle"
                aria-label="关闭界面动效"
                title="关闭动效"
              >
                <Pause className="size-3" />
              </button>
            </div>
          ) : null}
        </div>

        {/* content */}
        <main className="flex-1">{children}</main>
      </div>

      <WorkspaceUpgradeDialog
        open={upgradeDialogOpen}
        onOpenChange={setUpgradeDialogOpen}
        membership={session?.membership}
      />

      <Dialog open={editorEntryDialogOpen} onOpenChange={setEditorEntryDialogOpen}>
        <DialogContent className="max-w-[560px] gap-5 p-6 sm:rounded-2xl">
          <DialogHeader>
            <DialogTitle>进入视频剪辑</DialogTitle>
            <DialogDescription>
              选择已有项目继续剪辑，或新建一个空白视频编辑项目。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => {
                setEditorEntryDialogOpen(false)
                void navigate({ to: '/projects' })
              }}
              className="w-full rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-primary/50 hover:bg-accent"
            >
              <div className="text-sm font-semibold text-foreground">继续编辑已有项目</div>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                查看本地工作区中的视频编辑项目，并继续处理时间线。
              </p>
            </button>
            <button
              type="button"
              onClick={() => {
                setEditorEntryDialogOpen(false)
                void navigate({ to: '/projects/new' })
              }}
              className="w-full rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-primary/50 hover:bg-accent"
            >
              <div className="text-sm font-semibold text-foreground">新建空白视频编辑项目</div>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                创建新项目后进入专业编辑器，开始剪辑。
              </p>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={mcpDialogOpen} onOpenChange={setMcpDialogOpen}>
        <DialogContent className="w-[min(96vw,920px)] max-w-[920px] gap-0 overflow-hidden p-0 sm:rounded-2xl">
          <DialogHeader className="border-b border-border px-6 py-4 pr-14">
            <DialogTitle>JIAN MCP</DialogTitle>
            <DialogDescription>
              连接 Codex、Cursor 等 MCP 客户端，让 AI 协助完成视频工作流。
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[min(76vh,760px)] overflow-y-auto px-6 py-5">
            <LocalMcpStatusPanel />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
